import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  styled,
} from '@mui/material'
import { CampaignSetupFormValues, QUESTION_TYPE_LABELS } from '../types'
import StepNavigation from './StepNavigation'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1rem',
  color: theme.palette.secondary.dark,
  marginBottom: theme.spacing(1.5),
}))

const SummaryRow = ({
  label,
  value,
}: {
  label: string
  value: string | number | null | undefined
}) => (
  <Grid container sx={{ py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
    <Grid item xs={5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="caption" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
        {value || '–'}
      </Typography>
    </Grid>
  </Grid>
)

interface Props {
  values: CampaignSetupFormValues
  draftId: string | null
  onBack: () => void
  onSubmit: () => void
  isPublishing?: boolean
  orderDisplayId?: string
  jobName?: string
  readOnly?: boolean
}

const SummaryStep: React.FC<Props> = ({
  values,
  draftId,
  onBack,
  onSubmit,
  isPublishing,
  orderDisplayId,
  jobName,
  readOnly,
}) => {
  const campaignName = `${orderDisplayId || ''} - ${jobName || ''}`.trim() || 'Campaign'
  const adSetName = `${campaignName} - AdSet`
  const budgetLabel = values.amount !== '' ? `${values.budgetType}: €${values.amount}` : '–'
  const ageLabel = `${values.targetingAgeMin}–${values.targetingAgeMax}`
  const isLeadGeneration = values.campaignObjective === 'LEAD_GENERATION'

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Summary
            </Typography>
            {draftId ? (
              <Chip label="Draft saved" size="small" color="success" variant="outlined" />
            ) : (
              <Chip label="Not saved yet" size="small" color="warning" variant="outlined" />
            )}
          </Box>

          {/* 1. Platform */}
          <SectionTitle>1. Platform</SectionTitle>
          <SummaryRow label="platform" value={values.platforms[0]} />
          <SummaryRow label="ad_account_id" value={values.adAccountId || '–'} />
          <SummaryRow label="social_page_id" value={values.socialPageId || '–'} />

          <Divider sx={{ my: 2 }} />

          {/* 2. Campaign */}
          <SectionTitle>2. Campaign</SectionTitle>
          <SummaryRow label="name" value={campaignName} />
          <SummaryRow label="campaign_objective" value={values.campaignObjective} />
          <SummaryRow label="status" value="PAUSED" />
          <SummaryRow label="special_ad_categories" value="EMPLOYMENT" />

          <Divider sx={{ my: 2 }} />

          {/* 3. Ad Set */}
          <SectionTitle>3. Ad Set</SectionTitle>
          <SummaryRow label="name" value={adSetName} />
          <SummaryRow label="performance_goal" value={values.performanceGoal} />
          <SummaryRow label="destination_type" value={values.destinationType} />
          <SummaryRow label="budget" value={budgetLabel} />
          <SummaryRow label="start_time" value={values.startTime} />
          <SummaryRow label="end_time" value={values.endTime} />
          <SummaryRow
            label="targeting_location"
            value={
              values.targetingCities.length > 0
                ? values.targetingCities.map((c) => `${c.name} (${c.radius}km)`).join(', ')
                : 'Germany (DE) — all'
            }
          />
          <SummaryRow label="targeting_age" value={ageLabel} />
          <SummaryRow label="placements" value="ADVANTAGE_PLUS" />
          <SummaryRow label="status" value="PAUSED" />

          <Divider sx={{ my: 2 }} />

          {/* 4. Lead Form (only for Lead Generation) */}
          {isLeadGeneration && (
            <>
              <SectionTitle>4. Lead Form</SectionTitle>
              <SummaryRow label="name" value={values.leadFormName} />
              <SummaryRow label="privacy_policy_url" value={values.privacyPolicyUrl} />
              <SummaryRow label="intro_headline" value={values.introHeadline} />
              <SummaryRow
                label="questions"
                value={`${values.contactQuestions.length} question(s)`}
              />
              <SummaryRow label="thank_you_title" value={values.thankYouTitle} />
              <Box sx={{ mt: 1, pl: 1 }}>
                {values.contactQuestions.map((q, i) => (
                  <Typography key={q.id} variant="caption" color="text.secondary" display="block">
                    {i + 1}. {QUESTION_TYPE_LABELS[q.type]}
                    {q.required ? ' *' : ''}
                  </Typography>
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* 5. Ad Creative */}
          <SectionTitle>{isLeadGeneration ? '5.' : '4.'} Ad Creative</SectionTitle>
          <SummaryRow label="name" value={values.adName} />
          <SummaryRow label="creative_type" value={values.creativeType} />
          <SummaryRow label="message" value={values.primaryText} />
          <SummaryRow label="headline" value={values.headline} />
          <SummaryRow label="call_to_action" value={values.ctaType} />
          <SummaryRow label="media" value={`${values.mediaAssets.length} file(s)`} />
        </CardContent>
      </SectionCard>

      <StepNavigation
        onBack={onBack}
        onNext={onSubmit}
        isLastStep
        onSubmit={onSubmit}
        nextLabel={
          isPublishing ? (
            <CircularProgress size={18} color="inherit" />
          ) : readOnly ? (
            'Published'
          ) : (
            'Create Campaign'
          )
        }
        submitDisabled={isPublishing || readOnly}
      />
    </Box>
  )
}

export default SummaryStep
